/**
 * ClientSheet — the shared CWR glass bottom-sheet shell (profile-tab-reskin).
 *
 * Extracted from the shipped BookingDetailPage cancel-sheet idiom so the three
 * client confirm/edit sheets share ONE implementation. `open`-controlled: the
 * parent toggles `open`; the sheet plays an entrance animation on mount and an
 * EXIT animation before it unmounts (fixing the shipped cancel sheet's instant
 * unmount). Overlay tap, Esc, and an optional grab drag-to-dismiss all call
 * `onClose` (the parent flips `open`) — gated by `dismissable` so a mutation
 * in-flight can't dismiss the sheet mid-write.
 *
 * Body content is the consumer's children; shared body atoms (copy/actions/keep/
 * danger/save/cancel/error) live in ClientSheet.module.css and are imported by the
 * consumers. See docs/mockups/client-dashboard-redesign/design-system.md →
 * "Exception — shipped components".
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useKeyboardAwareScroll } from '@/hooks/useKeyboardAwareScroll';
import { useViewportSource } from '@/hooks/viewportSource';
import styles from './ClientSheet.module.css';

/** Exit-animation duration — keep in sync with `.sheetOut` / `.overlayOut` in the CSS. */
const CLOSE_MS = 260;
/** Downward drag (px) past which a release dismisses the sheet. */
const DISMISS_THRESHOLD = 110;
/** Fallback keyboard height (px) for the very first focus, before a real height is locked. */
const KEYBOARD_ESTIMATE_PX = 300;
/** Keyboard open/close animation window (ms) — length of the iOS keyboard curve we drive in JS. */
const KEYBOARD_ANIM_MS = 250;
/**
 * Keep the follow loop alive at least this long (from the phase start) before it's allowed to
 * settle and stop. WKWebView's real viewport event lands late (~end of the open), so the loop
 * must still be running that frame to cancel the anchor move — see the loop below.
 */
const KEYBOARD_SETTLE_GUARD_MS = 450;
/** Below this per-frame delta (px) the lift is considered stable for settle-detection. */
const KEYBOARD_SETTLE_EPS = 0.5;
/** Geometry equal within this (px) counts as "unchanged" when detecting the real keyboard event. */
const KEYBOARD_DEVIATION_EPS = 1;
/**
 * If focus never moves the viewport within this window, assume no on-screen keyboard appeared
 * (touch device with a hardware/Bluetooth keyboard) and run the held sheet back down.
 */
const KEYBOARD_NO_SHOW_MS = 700;
/** Drag snap-back animation (ms) when a release doesn't cross the dismiss threshold. */
const DRAG_SNAPBACK_MS = 220;

/**
 * Compile a cubic-bezier(p1x,p1y,p2x,p2y) timing function into `progress → eased` (Newton-
 * refined inverse of x(t)). We drive the keyboard follow in JS per-frame rather than via a CSS
 * transition, so we need to sample the curve ourselves.
 */
function makeCubicBezier(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
): (x: number) => number {
  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 5; i++) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-3) break;
      const d = slopeX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    return sampleY(t);
  };
}
/** Approx iOS keyboard open/close curve. */
const KEYBOARD_EASE = makeCubicBezier(0.17, 0.59, 0.4, 0.77);

export interface ClientSheetProps {
  /** Parent-controlled desired visibility. */
  open: boolean;
  /** Dismissal request (overlay tap / Esc / drag). The parent flips `open` to false. */
  onClose: () => void;
  /** Accent eyebrow above the title. */
  kicker?: string;
  /** Serif sheet title. */
  title?: string;
  /** When false, overlay/Esc/drag can't dismiss (e.g. a mutation is in flight). Default true. */
  dismissable?: boolean;
  /** Enable grab-handle drag-to-dismiss. Default true; opt out explicitly when needed. */
  draggable?: boolean;
  /** Accessible name when there is no visible `title`. */
  ariaLabel?: string;
  /** Test id on the sheet container (grab handle gets `${testId}-grab`). */
  testId?: string;
  children: ReactNode;
}

export function ClientSheet({
  open,
  onClose,
  kicker,
  title,
  dismissable = true,
  draggable = true,
  ariaLabel,
  testId,
  children,
}: ClientSheetProps) {
  const [rendered, setRendered] = useState(open);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<number | null>(null);
  const dragYRef = useRef(0);
  // rAF handle for the drag snap-back tween (drag owns `--drag-y`; the keyboard loop owns
  // `--kb-lift`; the CSS class composes both — one writer per custom property).
  const dragTweenRaf = useRef(0);
  // Whether a field inside the sheet currently holds focus — gates viewport-driven lift so
  // a stray viewport event with no keyboard up can't translate the resting sheet.
  const focusedRef = useRef(false);
  // Telegram's viewport inside the Mini App, none on the website (viewportSource.ts).
  const getViewport = useViewportSource();

  // The sheet is portalled outside ClientShell's page scroller, so it needs its
  // own keyboard-aware scroll rail for lower fields on iOS.
  useKeyboardAwareScroll(sheetRef, { enabled: rendered });

  // Keyboard-follow. WKWebView gives NO per-frame signal while the native keyboard animates
  // (Telegram `viewportChanged` / `visualViewport.resize` land as ONE discrete event at/near the
  // END of the ~250ms open, and Telegram may resize the webview across several late frames). So
  // the follow runs as a per-frame rAF loop that writes the sheet transform SYNCHRONOUSLY — no
  // React state, no rAF→setState hop. That indirection is what caused the settle pop: the browser
  // could paint the reflowed anchor with a still-stale transform before React committed.
  //
  // Every frame reads LIVE geometry and computes the lift the same frame it writes it:
  //   layoutBottom  = window.innerHeight              (where `bottom:0` anchors)
  //   visibleBottom = visualViewport.offsetTop+height (min'd with Telegram viewportHeight)
  //
  // LATE-ANCHOR MODEL (Telegram iOS): the real viewport change — visualViewport shrink AND/OR the
  // webview `innerHeight` drop — lands LATE, after the native keyboard has finished animating, and
  // the two sources can even move at different times. So live geometry is provably STALE until it
  // moves. We snapshot it at focus and treat the first ≥1px deviation of EITHER source as the
  // signal that reality has arrived.
  //
  // OPEN: during the 250ms curve we animate a PREDICTED visible bottom and take the lower of it
  // and live — lift = layoutBottom − min(predicted, live) — so prediction leads and reality takes
  // over the instant it deviates. Post-curve, if geometry is still exactly the snapshot we HOLD at
  // predictedKb (never trust the stale zero, which would drop the sheet behind the keyboard); the
  // moment either source deviates we latch to pure live (lift = layoutBottom − visibleBottom) for
  // the rest of the phase. Because layoutBottom is read the SAME frame it's applied, an anchor jump
  // of any size is cancelled exactly — no pop, no dip. If nothing ever deviates within
  // KEYBOARD_NO_SHOW_MS (hardware keyboard) we run the sheet back down.
  //
  // CLOSE: predicting in lift-space animates nothing when the anchor (not a lift) was holding the
  // sheet up, so we predict in VISIBLE-BOTTOM space instead: desiredVB(t) rises from the current
  // effective bottom to the resting full-height bottom on the curve, and lift = layoutBottom −
  // desiredVB read LIVE (NOT clamped ≥0 — lift goes negative to ride the keyboard down past a still
  // raised anchor). When the webview restores late, layoutBottom grows and the same formula
  // converges lift → 0 that frame, no jump. The loop stops after the lift converges to ~0 or the
  // settle guard expires; viewport events correct synchronously and restart it for later changes.
  useEffect(() => {
    if (!rendered) return;
    const sheet = sheetRef.current;
    if (!sheet) return;

    const webApp = getViewport();
    // Predict only where an on-screen keyboard exists — desktop / hardware-keyboard clients
    // (no touch) never raise one, so focus must NOT lift the sheet there.
    const predictiveEnabled = (navigator.maxTouchPoints ?? 0) > 0;

    const isFormEl = (el: EventTarget | null): el is HTMLElement =>
      el instanceof HTMLElement &&
      (el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.getAttribute('contenteditable') === 'true');

    const writeClamp = () =>
      sheet.style.setProperty(
        '--client-sheet-visible-height',
        `${window.visualViewport?.height ?? webApp?.viewportHeight ?? window.innerHeight}px`,
      );

    const readLive = () => {
      const layoutBottom = window.innerHeight;
      const vv = window.visualViewport;
      let visibleBottom = vv ? vv.offsetTop + vv.height : layoutBottom;
      const tg = webApp?.viewportHeight;
      if (typeof tg === 'number' && tg > 0) visibleBottom = Math.min(visibleBottom, tg);
      return { layoutBottom, visibleBottom };
    };

    // Last-locked measured keyboard height (useVisualViewportCssVars writes it on <html> after
    // the first-ever open); 0 until then. Seeds the predicted rise on the first-ever focus.
    const readMeasuredKeyboard = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-keyboard-height');
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };

    // ---- Loop state (closure, not React) --------------------------------------------------
    let raf = 0;
    let phase: 'open' | 'close' | null = null;
    let phaseStart = 0;
    let predictedKb = 0;
    let currentLift = 0;
    let settleFrames = 0;
    let resetTimer: number | undefined;
    // Focus-time geometry snapshot + the latch that flips OPEN from prediction/hold to live once
    // reality deviates from it (see the effect header).
    let startLayoutBottom = 0;
    let startVisibleBottom = 0;
    let liveLatched = false;
    // Visible-bottom endpoints for the CLOSE curve (screen-space, so it animates even when the
    // anchor — not a lift — was holding the sheet up).
    let closeStartVB = 0;
    let closeEndVB = 0;

    const changed = (a: number, b: number) => Math.abs(a - b) > KEYBOARD_DEVIATION_EPS;

    const writeLift = (px: number) => {
      currentLift = px;
      sheet.style.setProperty('--kb-lift', `${px}px`);
    };
    const stopLoop = () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    // Lift for the current phase/state at time `now`. Reads live geometry and latches liveLatched
    // as a side effect, so it's safe to call from both the rAF frame and the resize handler.
    const computeLift = (now: number) => {
      const elapsed = now - phaseStart;
      const t = Math.min(1, elapsed / KEYBOARD_ANIM_MS);
      const { layoutBottom, visibleBottom } = readLive();

      if (phase === 'close') {
        const desiredVB = closeStartVB + KEYBOARD_EASE(t) * (closeEndVB - closeStartVB);
        // NOT clamped ≥0 — negative lift rides the keyboard down past a still-raised anchor.
        return { lift: layoutBottom - desiredVB, t, elapsed, noShow: false };
      }

      // OPEN
      if (!liveLatched && (changed(layoutBottom, startLayoutBottom) || changed(visibleBottom, startVisibleBottom))) {
        liveLatched = true;
      }
      let lift: number;
      if (t < 1) {
        // Prediction leads the rise; `min` hands off to reality the instant it deviates.
        const predictedVisibleBottom = layoutBottom - KEYBOARD_EASE(t) * predictedKb;
        lift = layoutBottom - Math.min(predictedVisibleBottom, visibleBottom);
      } else if (liveLatched) {
        // Reality has arrived — track it directly (same-frame read+write cancels anchor jumps).
        lift = layoutBottom - visibleBottom;
      } else {
        // Post-curve but geometry still exactly the snapshot → provably stale → HOLD, never the
        // stale zero (which would drop the sheet back behind the keyboard).
        lift = predictedKb;
      }
      return { lift: Math.max(0, lift), t, elapsed, noShow: !liveLatched && t >= 1 };
    };

    const beginClose = (now: number) => {
      phase = 'close';
      phaseStart = now;
      const { layoutBottom } = readLive();
      closeStartVB = layoutBottom - currentLift; // current effective sheet bottom (screen space)
      closeEndVB = startLayoutBottom; // resting full-height bottom captured at focus
      settleFrames = 0;
    };

    const frame = (now: number) => {
      const r = computeLift(now);
      const moved = Math.abs(r.lift - currentLift);
      writeLift(r.lift);

      if (phase === 'close') {
        if (r.t >= 1 && (Math.abs(r.lift) < KEYBOARD_SETTLE_EPS || r.elapsed >= KEYBOARD_SETTLE_GUARD_MS)) {
          writeLift(0);
          stopLoop();
          return;
        }
      } else if (r.noShow && r.elapsed >= KEYBOARD_NO_SHOW_MS) {
        // No on-screen keyboard ever materialised — run the held sheet back down.
        beginClose(now);
      } else if (liveLatched && r.elapsed >= KEYBOARD_SETTLE_GUARD_MS && moved < KEYBOARD_SETTLE_EPS) {
        // Reality arrived, past the late-event window, no longer moving → settle. Viewport events
        // restart the loop if anything shifts afterwards (keyboard type/height switch, orientation).
        if (++settleFrames >= 2) {
          stopLoop();
          return;
        }
      } else {
        settleFrames = 0;
      }
      raf = window.requestAnimationFrame(frame);
    };

    const startLoop = () => {
      if (!raf) raf = window.requestAnimationFrame(frame);
    };
    // Restart the loop (in live-tracking mode — skip the rise curve) when a viewport event fires
    // after it already settled while a field stays focused.
    const kickLive = () => {
      if (!focusedRef.current || !predictiveEnabled || raf) return;
      phase = 'open';
      phaseStart = performance.now() - KEYBOARD_ANIM_MS; // curve already done → live tracking
      liveLatched = true;
      settleFrames = 0;
      startLoop();
    };

    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as EventTarget | null;
      if (!isFormEl(el) || !sheet.contains(el)) return; // only fields inside THIS sheet
      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = undefined;
      }
      if (focusedRef.current) return; // field-to-field: keyboard stays up, keep the current lift
      focusedRef.current = true;
      if (!predictiveEnabled) return;
      predictedKb = readMeasuredKeyboard() || KEYBOARD_ESTIMATE_PX;
      const snap = readLive();
      startLayoutBottom = snap.layoutBottom;
      startVisibleBottom = snap.visibleBottom;
      liveLatched = false;
      phase = 'open';
      phaseStart = performance.now();
      settleFrames = 0;
      startLoop();
    };
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as EventTarget | null;
      if (isFormEl(next) && sheet.contains(next)) return; // field-to-field: keyboard stays, keep lift
      // WKWebView sometimes reports a null relatedTarget on field-to-field, so confirm on a short
      // delay that focus really left the sheet before running the sheet back down.
      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = window.setTimeout(() => {
        resetTimer = undefined;
        if (isFormEl(document.activeElement) && sheet.contains(document.activeElement)) return;
        focusedRef.current = false;
        if (!predictiveEnabled) return;
        beginClose(performance.now());
        startLoop();
      }, 60);
    };

    const onViewport = () => {
      writeClamp();
      if (raf) {
        // Belt-and-braces: some WKWebView builds paint the reflow before the pending rAF fires, so
        // correct synchronously in the resize handler too (idempotent with the next frame's write).
        writeLift(computeLift(performance.now()).lift);
      } else {
        kickLive();
      }
    };

    writeClamp(); // seed the clamp var (lift stays 0 until a field is focused)
    sheet.addEventListener('focusin', onFocusIn);
    sheet.addEventListener('focusout', onFocusOut);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onViewport);
    vv?.addEventListener('scroll', onViewport);
    window.addEventListener('resize', onViewport);
    webApp?.onEvent('viewportChanged', onViewport);

    return () => {
      sheet.removeEventListener('focusin', onFocusIn);
      sheet.removeEventListener('focusout', onFocusOut);
      vv?.removeEventListener('resize', onViewport);
      vv?.removeEventListener('scroll', onViewport);
      window.removeEventListener('resize', onViewport);
      webApp?.offEvent('viewportChanged', onViewport);
      stopLoop();
      if (resetTimer) clearTimeout(resetTimer);
      focusedRef.current = false;
    };
  }, [getViewport, rendered]);

  // Cancel any in-flight drag snap-back tween on unmount.
  useEffect(
    () => () => {
      if (dragTweenRaf.current) cancelAnimationFrame(dragTweenRaf.current);
    },
    [],
  );

  // Mount on open; on close, play the exit animation THEN unmount.
  useEffect(() => {
    if (open) {
      setRendered(true);
      return;
    }
    if (!rendered) return;
    const t = window.setTimeout(() => {
      setRendered(false);
      dragYRef.current = 0; // element unmounts; --drag-y / --kb-lift reset with the fresh node
    }, CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [open, rendered]);

  const requestDismiss = useCallback(() => {
    if (dismissable) onClose();
  }, [dismissable, onClose]);

  // Esc dismiss (desktop/dev convenience; harmless on device).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, requestDismiss]);

  if (!rendered) return null;

  const closing = !open;

  // Drag owns the `--drag-y` custom property; the keyboard loop owns `--kb-lift`; the CSS class
  // composes both into one transform. Writes go straight to the element (no React state on the
  // gesture path), and the entrance/exit keyframes still override the transform while they run.
  const writeDragVar = (px: number) => {
    sheetRef.current?.style.setProperty('--drag-y', `${px}px`);
  };
  const cancelDragTween = () => {
    if (dragTweenRaf.current) {
      cancelAnimationFrame(dragTweenRaf.current);
      dragTweenRaf.current = 0;
    }
  };

  const onGrabDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dismissable || !draggable) return;
    cancelDragTween();
    dragStart.current = event.clientY;
    dragYRef.current = 0;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onGrabMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStart.current == null) return;
    const nextDragY = Math.max(0, event.clientY - dragStart.current);
    dragYRef.current = nextDragY;
    writeDragVar(nextDragY);
  };
  const onGrabUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStart.current == null) return;
    const dismissed = dragYRef.current > DISMISS_THRESHOLD;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    dragStart.current = null;
    if (dismissed) {
      // Leave `--drag-y` where it is; the sheetOut keyframe overrides the transform on exit.
      dragYRef.current = 0;
      requestDismiss();
      return;
    }
    // Spring back to rest in JS. There is deliberately NO CSS transform transition (the keyboard
    // loop needs its per-frame writes to land immediately), so we animate the release ourselves.
    const startY = dragYRef.current;
    const t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / DRAG_SNAPBACK_MS);
      const value = startY * (1 - KEYBOARD_EASE(p));
      dragYRef.current = value;
      writeDragVar(value);
      if (p < 1) {
        dragTweenRaf.current = requestAnimationFrame(step);
      } else {
        dragTweenRaf.current = 0;
        dragYRef.current = 0;
        writeDragVar(0);
      }
    };
    dragTweenRaf.current = requestAnimationFrame(step);
  };

  return createPortal(
    <>
      <div
        className={`${styles.overlay} ${closing ? styles.overlayOut : ''}`}
        onClick={requestDismiss}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${closing ? styles.sheetOut : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        data-testid={testId}
      >
        {draggable ? (
          <div
            className={styles.dragRegion}
            onPointerDown={onGrabDown}
            onPointerMove={onGrabMove}
            onPointerUp={onGrabUp}
            onPointerCancel={onGrabUp}
            data-testid={testId ? `${testId}-grab` : undefined}
          >
            <div className={`${styles.grab} ${styles.grabDraggable}`} />
            {kicker && <div className={styles.kicker}>{kicker}</div>}
            {title && <div className={styles.title}>{title}</div>}
          </div>
        ) : (
          <>
            <div className={styles.grab} aria-hidden="true" />
            {kicker && <div className={styles.kicker}>{kicker}</div>}
            {title && <div className={styles.title}>{title}</div>}
          </>
        )}
        {children}
      </div>
    </>,
    document.body,
  );
}
